import { useState, useEffect } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import 'flag-icons/css/flag-icons.min.css';
import './TTS.css';

const currencies = ['USD', 'EUR', 'USDC', 'EURC', 'USDT', 'XAF', 'XOF', 'PYUSD'];

const whyCards = [
  {
    title: 'Multi-Currency Wallets',
    desc: 'Manage traditional and digital currencies from a single treasury platform.',
  },
  {
    title: 'Cross-Border Payments',
    desc: 'Support efficient transfers and settlement across markets.',
  },
  {
    title: '24/7 Availability',
    desc: 'Always-on infrastructure designed for modern financial operations.',
  },
  {
    title: 'Transparency & Security',
    desc: 'Built with institutional-grade controls and auditable transaction records.',
  },
];

const flags = [
  { code: 'us' },
  { code: 'ca' },
  { code: 'gb' },
  { code: 'eu' },
  { code: 'gh' },
  { code: 'ng' },
  { code: 'ci' },
];

const categories = [
  {
    label: 'Importers',
    desc: 'Get paid by clients in the U.S. or EU using familiar methods — receive funds in USDC and keep control of your earnings.',
    tags: ['Payment Links', 'ACH', 'Wire & SEPA', 'Multi-chain'],
    img: '/importers.webp',
  },
  {
    label: 'Exporters',
    desc: 'Send and receive international payments with low fees and fast settlement — no need for a bilateral bank.',
    tags: ['Invoices', 'Treasury'],
    img: '/exporters.webp',
  },
  {
    label: 'Expats / African Diaspora',
    desc: 'Move money easily between countries, manage bills abroad, and support family without high transfer costs.',
    tags: ['Remittance', 'Multi-currency'],
    img: '/expats.webp',
  },
  {
    label: 'Fintechs',
    desc: 'Scale global payouts to creators, vendors, and users using digital dollars — with compliance and automation built in.',
    tags: ['API', 'Payouts', 'Stablecoins'],
    img: '/fintechs.webp',
  },
];

const TTS = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Azamra Treasury &amp; Trade Solutions</title>
        <meta name="description" content="Manage multi-currency wallets, enable cross-border payments, and connect traditional finance with digital assets. Azamra supports institutional treasury operations across Africa with secure and scalable financial infrastructure." />
        <meta property="og:title" content="Azamra Treasury & Trade Solutions" />
        <meta property="og:description" content="Manage multi-currency wallets, enable cross-border payments, and connect traditional finance with digital assets." />
        <meta property="og:url" content="https://www.azamra.com/treasury-solutions" />
        <link rel="canonical" href="https://www.azamra.com/treasury-solutions" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "FinancialProduct",
            "name": "Azamra Treasury & Trade Solutions",
            "description": "Multi-currency wallet supporting USD, EUR, USDC, EURC, USDT, XAF, XOF, PYUSD with cross-border payment capabilities connecting traditional finance with digital assets.",
            "provider": { "@type": "Organization", "name": "Azamra" },
            "areaServed": ["US", "CA", "GB", "EU", "GH", "NG", "CI"]
          }
        `}</script>
      </Helmet>
      <Header />

      {/* ── SECTION 1: HERO ── */}
      <section className="tts-hero-section" style={{ backgroundImage: `url('/assets/images/hero-bg3-tts.webp')` }}>
        <div className="tts-hero-overlay" />

        {/* Wallet + currency chips orbiting it (desktop) */}
        <div className="tts-hero-visual" aria-hidden="true">
          <img
            src="/assets/images/tts-wallet-glow.webp"
            alt=""
            className="tts-hero-wallet-img"
          />
          {currencies.map((cur, i) => (
            <span className={`tts-orbit-chip tts-orbit-chip-${i + 1}`} key={cur}>
              {cur}
            </span>
          ))}
        </div>

        <Container fluid className="tts-hero-container">
          <Row className="align-items-center tts-hero-row">

            {/* Left: Text */}
            <Col lg={7} md={12} className="tts-hero-left">
              <h1 className="tts-hero-title">
                Azamra Treasury &amp;<br />Trade Solutions
              </h1>
              <p className="tts-hero-subtitle">
                Manage multi-currency wallets, enable cross-border payments, and
                connect traditional finance with digital assets. Azamra supports
                institutional treasury operations across Africa with secure and
                scalable financial infrastructure.
              </p>
              <div className="tts-hero-btn-group">
                <a
                  href="https://treasury.azamra.com/signup"
                  className="tts-hero-btn tts-hero-btn-primary"
                >
                  Create account
                </a>
                <a
                  href="https://treasury.azamra.com/login"
                  className="tts-hero-btn tts-hero-btn-secondary"
                >
                  Sign in
                </a>
              </div>
              <div className="tts-hero-currencies">
                {currencies.map((cur) => (
                  <span className="tts-currency-chip" key={cur}>{cur}</span>
                ))}
              </div>
              <img
                src="/assets/images/tts-wallet-glow.webp"
                alt="Azamra digital wallet"
                className="tts-hero-wallet-sm"
              />
            </Col>

          </Row>

          {/* ── SECTION 2: AVAILABLE IN ── */}
          <div className="tts-flags-row">
            <span className="tts-flags-label">AVAILABLE IN:</span>
            <div className="tts-flags-list">
              {flags.map((f) => (
                <div className="tts-flag-circle" key={f.code} title={f.code.toUpperCase()}>
                  <span className={`fi fi-${f.code} fis tts-flag-icon`} />
                </div>
              ))}
              <div className="tts-flag-more">+</div>
            </div>
          </div>

        </Container>
      </section>

      {/* ── SECTION 3: WHY AZAMRA ── */}
      <section className="tts-why-section">
        <Container>
          <h2 className="tts-why-title">Why Azamra Treasury &amp; Trade Solutions?</h2>
          <Row className="tts-why-row">
            {whyCards.map((card) => (
              <Col lg={3} md={6} xs={12} className="tts-why-col" key={card.title}>
                <div className="tts-why-card">
                  <h3 className="tts-why-card-title">{card.title}</h3>
                  <p className="tts-why-card-desc">{card.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── SECTION 4: USER CATEGORIES ── */}
      <section className="tts-categories-section">
        <Container>
          <Row className="tts-categories-row">
            {categories.map((cat) => (
              <Col lg={6} md={6} xs={12} className="tts-category-col" key={cat.label}>
                <div className="tts-category-card">
                  <img src={cat.img} alt={cat.label} className="tts-category-img" />
                  <div className="tts-category-body">
                    <h3 className="tts-category-title">{cat.label}</h3>
                    <p className="tts-category-desc">{cat.desc}</p>
                    <div className="tts-category-tags">
                      {cat.tags.map((tag) => (
                        <span className="tts-category-tag" key={tag}>
                          <svg width="10" height="10" viewBox="0 0 10 10" className="tts-tag-check">
                            <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#0038B6" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                          </svg>
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ── SECTION 5: GET YOUR CARD ── */}
      <section className="tts-card-section">
        <Container>
          <Row className="align-items-center tts-card-row">

            {/* Left: Card visuals */}
            <Col lg={6} md={12} className="tts-card-left">
              <img src="/hero.webp" alt="Azamra wallet and card" className="tts-cards-visual-img" />
            </Col>

            {/* Right: Text */}
            <Col lg={6} md={12} className="tts-card-right">
              <span className="tts-coming-soon-badge">Coming soon</span>
              <h2 className="tts-card-title">Get Your Card</h2>
              <p className="tts-card-desc">
                Spend your dollars anywhere with your Azamra card.
              </p>
              <div className="tts-card-checks">
                <span className="tts-card-check-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Virtual card
                </span>
                <span className="tts-card-check-item">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M3 8L6.5 11.5L13 4.5" stroke="#0038B6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  Global acceptance
                </span>
              </div>
            </Col>

          </Row>
        </Container>
      </section>

      {/* ── SECTION 6: BLUE CTA BANNER ── */}
      <section className="tts-cta-section">
        <Container>
          <div className="tts-cta-banner" style={{ backgroundImage: `url('/assets/images/${isMobile ? 'cta1.webp' : 'cta.webp'}')` }}>
            <h2 className="tts-cta-title">
              Take Control of Your Money, Wherever You Are.
            </h2>
            <p className="tts-cta-desc">
              From USD to EUR, banks to blockchains, Azamra makes sending, receiving,
              and holding stablecoins simple, fast, and secure.
            </p>
            <a href="https://treasury.azamra.com/signup" className="tts-cta-btn">
              Create an Account
            </a>
          </div>
        </Container>
      </section>

      <Footer />
    </>
  );
};

export default TTS;
